<template>
  <master-layout id="gic-content-home">
    <div
      v-show="!loading"
      class="lg:flex-1 lg:overflow-hidden relative font-aktiv-grotesk bg-white"
    >
      <div class="w-full">
        <LogoutPopup :show="showModal" @close-modal="closeModal"></LogoutPopup>
        <ProfileNav
          v-model:showModal="showModal"
          :bannerColor="'bg-grey-90'"
          :profileNameColor="'text-white'"
          :profileIconColor="'bg-[#FF6600]'"
          :conferenceType="'/gic'"
        />

        <div class="fixed bottom-[128px] z-50">
          <CheatConsole v-if="isNonProd"></CheatConsole>
        </div>

        <div
          class="flex justify-center gap-[29px] xs:gap-[45px] md:gap-[82px] px-[24px] lg:px-[54px] flex-none grow-0 order-none mt-[-55px] z-10"
        >
          <div class="flex flex-col items-center w-[75px] md:w-[96px]">
            <!-- bg-photodrop bg-cover -->
            <button
              id="photodropIcon"
              class="cursor-pointer h-[75px] w-[75px] md:h-[110px] md:w-[96px] my-[14px] md:my-0 flex items-center justify-center"
              data-event-tracking="gensapp-home-buttons-photodrop"
              data-test-id="gensapp-home-buttons-photodrop"
              @click="goToPhotoDrop()"
            >
              <img src="../../../../public/assets/images/gic/photodrop.svg" />
            </button>
            <div
              class="font-aktiv-grotesk-trial font-semibold not-italic text-base text-center text-grey-100 w-max whitespace-nowrap"
            >
              PhotoDrop
            </div>
          </div>
          <div class="flex flex-col items-center relative w-[75px] md:w-[96px]">
            <button
              class="cursor-pointer h-[75px] w-[75px] md:h-[110px] md:w-[96px] my-[14px] md:my-0 flex items-center justify-center"
              data-event-tracking="gensapp-home-buttons-update"
              data-test-id="gensapp-home-buttons-update"
              @click="goToAnnouncements()"
            >
              <img src="../../../../public/assets/images/gic/announcement.svg" />
            </button>
            <div v-if="hasUnreadAnnouncements" class="unread-count">
              <p>{{ unreadAnnouncementCount }}</p>
            </div>
            <div
              class="font-aktiv-grotesk-trial font-semibold not-italic text-base text-center text-grey-100 w-max whitespace-nowrap"
            >
              Updates
            </div>
          </div>
          <div class="flex flex-col items-center w-[75px] md:w-[96px]">
            <button
              id="interpretationIcon"
              class="cursor-pointer h-[75px] w-[75px] md:h-[110px] md:w-[96px] my-[14px] md:my-0 flex items-center justify-center"
              data-event-tracking="gensapp-home-buttons-interpretation"
              data-test-id="gensapp-home-buttons-interpretation"
              @click="goToInterpretation()"
            >
              <span
                class="flex h-[75px] w-[75px] shrink-0 items-center justify-center rounded-full bg-yellow shadow-[0_1px_10px_rgba(0,0,0,0.1)]"
              >
                <img
                  src="../../../../public/assets/icons/interpretation-gic.svg"
                  class="h-[55px] w-auto object-contain"
                  alt="Interpretation"
                />
              </span>
            </button>
            <div
              class="font-aktiv-grotesk-trial font-semibold not-italic text-base text-center text-grey-100 w-max whitespace-nowrap"
            >
              Interpretation
            </div>
          </div>
        </div>
      </div>
      <section ref="test" class="flex flex-col">
        <div
          v-for="(component, idx) in homePageSection"
          :key="`${homePageSectionListKey}-${idx}-${component.name}`"
        >
          <div
            v-if="showForDelegates(component.isPublic)"
            :ref="component.name"
            class="anchor-scroll-margin"
          >
            <html-to-vue :html="component.html" />
          </div>
        </div>
      </section>
    </div>
    <div v-show="loading" class="w-screen h-screen flex items-center justify-center">
      <loading-state />
    </div>
  </master-layout>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapState, mapActions } from 'vuex';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import LoadingState from '@/components/common/LoadingState.vue';
import CheatConsole from '@/components/common/CheatConsole.vue';
import LogoutPopup from '@/components/HomePage/LogoutPopup.vue';
import ProfileNav from '@/components/HomePage/ProfileNav.vue';
import platformMixin from '@/mixins/platformMixin';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { CONFERENCE_TYPES } from '@/utils/conference-type';
import {
  openInterpretationWebView,
  resolveInterpretationPage,
  resolveSrc,
} from '@/utils/interpretation-webview';
import {
  buildPhotodropMementoUrl,
  PHOTODROP_MEMENTO_URL_GIC,
  hasGicMementoAccess,
} from '@/utils/memento';

export default {
  name: 'IndexPage',
  mixins: [platformMixin],
  components: {
    CheatConsole,
    LoadingState,
    LogoutPopup,
    ProfileNav,
  },
  layout: 'default',
  data() {
    return {
      CONFERENCE_TYPES,
      events: [],
      cvpObj: {},
      homePageSection: [],
      /** Bumped after each homepage CMS fetch so v-for remounts when sections are added/removed/reordered. */
      homePageSectionListKey: 0,
      seatings: [],
      showModal: false,
      splashHidden: false,
      appStateListener: null,
      // Guards the openInterpretation deep-link watcher; component-local on
      // purpose — it only serializes THIS page's handler.
      interpDeepLinkInFlight: false,
    };
  },
  computed: {
    ...mapGetters('profile', ['type', 'isAbsentAlumni']),
    ...mapState('cms', ['loading', 'mementoEnable']),
    ...mapState('user', ['token']),
    ...mapGetters('cms', ['getAnnouncements']),
    // name is the raw profile state (DB name column), NOT the profile `name`
    // getter — memento must receive the full name, never the preferred name.
    ...mapState('profile', ['conferences', 'email', 'country', 'groupName', 'name']),
    ...mapState('social', ['profilePicture']),
    ...mapState('social', {
      displayName: 'name',
    }),

    // Normalized flag: the sysparams API can return a string, number, or bool.
    // Named distinctly from the raw `mementoEnable` state above (not just a
    // trailing "d") so the two aren't a one-character typo away from each other.
    isMementoLive() {
      return this.mementoEnable === '1' || this.mementoEnable === 1 || this.mementoEnable === true;
    },

    hasUnreadAnnouncements() {
      return this.getAnnouncements(CONFERENCE_TYPES.GIC).some(a => a.isNew);
    },
    unreadAnnouncementCount() {
      return this.getAnnouncements(CONFERENCE_TYPES.GIC).filter(a => a.isNew).length;
    },
    isNonProd() {
      return process.env.VUE_APP_NODE_ENV !== 'production';
    },
    myName() {
      return this.displayName ? this.displayName : this.name;
    },
    showProfilePic() {
      return this.profilePicture;
    },
    conferencePassCode() {
      const gicConferences = this.conferences.filter(conference => conference.type === 'GIC');
      const currentConference = gicConferences.find(conference => conference.currentConference);
      if (currentConference) return currentConference.conferencePassCode;

      return gicConferences.reduce((latestConference, conference) => {
        if (!latestConference || conference.year > latestConference.year) return conference;
        if (conference.year === latestConference.year && conference.id > latestConference.id) {
          return conference;
        }
        return latestConference;
      }, null)?.conferencePassCode;
    },
  },
  watch: {
    '$route.hash': {
      handler(newHash, oldHash) {
        // This watcher triggers when the hash (e.g., #schedule, #experience) changes
        // More reliable in Ionic than beforeRouteUpdate
        console.log('Hash changed from', oldHash, 'to', newHash);

        // Scroll to the section when hash changes
        if (newHash && newHash !== oldHash) {
          this.$nextTick(() => {
            this.scrollToSlugSection();
          });
        }
      },
      immediate: false,
    },
    // Deep link into the Interpretation WebView (issue #278), ported from
    // src/pages/home/index.vue for GIC delegates:
    // /gic/home?openInterpretation=listener[&src=qr|push|cms]. immediate covers
    // cold start (query present at creation); the watcher covers warm arrivals
    // (Ionic keeps this page alive, so a new push here changes $route.query).
    '$route.query.openInterpretation': {
      immediate: true,
      async handler(value) {
        // undefined = param absent. Empty string / arrays / unknown values flow
        // through resolveInterpretationPage and fall back to 'home'.
        if (value === undefined || this.interpDeepLinkInFlight) return;
        // Ionic caches this page and $route is the GLOBAL route, so this
        // watcher also fires when some OTHER route gains the param (e.g. a
        // crafted /schedule?openInterpretation=...). Only act when the query
        // arrived on this page's own path. Normalized: vue-router's default
        // non-strict matching also renders this page for /gic/home/ and
        // /GIC/Home (realistic in printed QR URLs).
        const arrivalPath = this.$route.path.replace(/\/+$/, '').toLowerCase();
        if (arrivalPath !== '/gic/home') return;
        this.interpDeepLinkInFlight = true;
        try {
          const page = resolveInterpretationPage(value);
          const src = resolveSrc(this.$route.query.src ?? null);
          // Strip BEFORE opening: a crash below can then never leave a
          // self-re-triggering URL in history.
          await this.stripInterpretationParams();
          // The strip is a guarded navigation — beforeEach may redirect it.
          // Conference remap to /home | /gpcr/home is documented and fine;
          // anything else (logout to /login, access-denied) must NOT get the
          // WebView opened on top of it. The query re-check also covers a
          // guard ABORT (NavigationFailure resolves, route unchanged): the
          // strip-before-open invariant must hold before anything opens.
          const landedPath = this.$route.path.replace(/\/+$/, '').toLowerCase();
          if (!landedPath.endsWith('/home')) return;
          if (this.$route.query.openInterpretation !== undefined) return;
          await openInterpretationWebView(page, {
            src,
            conferencePassCode: this.conferencePassCode,
          });
        } catch (e) {
          // Failure UX: the user stays on Home — the Interpretation button IS
          // the retry path.
          this.$store.dispatch(
            'toast/error',
            "Couldn't open interpretation. Tap the Interpretation button to try again.",
          );
          console.error('interpretation deeplink failed', e);
        } finally {
          // A failed open must never block future deep links (a second tap in
          // the same session is a supported flow).
          this.interpDeepLinkInFlight = false;
          // An arrival dropped while we were in flight leaves its query in the
          // route, and an identical future link would be a duplicate navigation
          // the watcher never sees — strip it so later links stay live (drop
          // policy: no second open for the dropped arrival itself).
          if (
            this.$route.query.openInterpretation !== undefined &&
            this.$route.path.endsWith('/home')
          ) {
            try {
              await this.stripInterpretationParams();
            } catch {
              // noop
            }
          }
        }
      },
    },
  },

  async ionViewWillEnter() {
    await this.refreshGicHomeData();
  },

  async ionViewDidEnter() {
    if (this.appStateListener) {
      await this.appStateListener.remove();
      this.appStateListener = null;
    }
    this.appStateListener = await CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await this.refreshGicHomeData();
      }
    });

    if (this.$refs.scheduleSection) {
      this.$refs.scheduleSection[0].setClosestEvent();
    }
    this.$nextTick(async () => {
      if (!this.splashHidden) {
        await SplashScreen.hide();
        this.splashHidden = true;
      }
      this.setLoading(false);
      setTimeout(async () => {
        await this.scrollToSlugSection();
      }, 500);
    });
    FirebaseAnalytics.setScreenName({
      screenName: 'home',
      nameOverride: 'HomePage',
    });
  },
  async ionViewDidLeave() {
    if (this.appStateListener) {
      await this.appStateListener.remove();
      this.appStateListener = null;
    }
    if (this.$refs.kickOffSection) {
      this.$refs.kickOffSection[0].pause();
    }
  },
  methods: {
    ...mapActions('cms', ['setLoading']),
    async refreshGicHomeData() {
      await Promise.all([
        this.$store.dispatch('cms/fetchAnnouncements', {
          locale: 'en',
          conferenceType: CONFERENCE_TYPES.GIC,
          force: true,
        }),
        this.$store.dispatch('announcements/fetchUnreadCount', CONFERENCE_TYPES.GIC),
        this.$store.dispatch('social/fetchSocialProfile'),
        this.$store.dispatch('signup/fetchSignup'),
        this.$store.dispatch('signup/fetchDates'),
        this.$store.dispatch('cms/fetchRegistrationLink', {
          locale: 'en',
          conferenceType: CONFERENCE_TYPES.GIC,
        }),
        this.$store.dispatch('filter/fetchWatchLists', {
          locale: 'en',
          conferenceType: CONFERENCE_TYPES.GIC,
        }),
        this.fetchHomePage(),
      ]);
    },
    logEvent(event) {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: event,
          page: 'home',
        },
      });
    },
    closeModal() {
      this.showModal = false;
    },
    goToPhotoDrop() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-photodrop',
          page: 'home',
        },
      });
      if (this.isMementoLive && hasGicMementoAccess(this.conferences)) {
        window.open(
          buildPhotodropMementoUrl({
            name: this.name,
            email: this.email,
            country: this.country,
            groupName: this.groupName,
            baseUrl: PHOTODROP_MEMENTO_URL_GIC,
          }),
          '_blank',
        );
        return;
      }
      this.$router.push('/gic/photodrop');
    },
    goToAnnouncements() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-update',
          page: 'home',
        },
      });
      this.$router.push('/gic/announcement');
    },
    // Strip this feature's deep-link params, keeping unrelated query keys and
    // the hash. One param list shared by the watcher's main flow and its
    // finally late-strip — no drift.
    stripInterpretationParams() {
      const { openInterpretation: _oi, src: _src, ...rest } = this.$route.query;
      return this.$router.replace({
        path: this.$route.path,
        query: rest,
        hash: this.$route.hash,
      });
    },
    async goToInterpretation() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-interpretation',
          page: 'home',
        },
      });
      try {
        await openInterpretationWebView('home', {
          conferencePassCode: this.conferencePassCode,
        });
      } catch (e) {
        this.$store.dispatch('toast/error', "Couldn't open interpretation. Please try again.");
        console.error('interpretation open failed', e);
      }
    },
    async fetchHomePage() {
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/gc-app-homepage-sections',
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
          params: {
            locale: 'en',
            conferenceType: CONFERENCE_TYPES.GIC,
            _ts: Date.now(),
          },
        },
      );
      const sections = Array.isArray(data) ? data : [];
      // Only re-render the homepage sections when the content actually changed —
      // reassigning + bumping the list key forces a full re-mount that resets the
      // ion-content scroll to top. Preserves scroll on a normal re-enter (e.g.
      // returning from the /url in-app browser redirect).
      if (JSON.stringify(sections) !== JSON.stringify(this.homePageSection)) {
        this.homePageSection = sections;
        this.homePageSectionListKey += 1;
      }
    },
    showForDelegates(isPublic) {
      return isPublic || !this.isAbsentAlumni;
    },
    async scrollToSlugSection() {
      const slug = this.$route.hash.slice(1);
      if (this.$refs[slug] && this.$refs[slug].length > 0) {
        await this.$refs[slug][0].scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
};
</script>

<style scoped>
.button {
  background: #fef8ec;
  height: 96px;
  width: 157.5px;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  margin-bottom: 4px;
  box-shadow: 0px 5px 5px #ccc;
  border-top: 1px solid #e9e9e9;
}

.unread-count {
  background: #ff6600;
  text-align: center;
  font: font-aktiv-grotesk-medium;
  size: 16px;
  line-height: 24px;
  color: #fef8ec;
  position: absolute;
  top: 5px;
  right: 0;
  width: 26.39px;
  height: 26.39px;
  border-radius: 50%;
}

h2 {
  @apply text-[32px] leading-[48px] font-aktiv-grotesk-medium text-blue;
}

@media all and (min-width: 768px) {
  .unread-count {
    top: 5px;
    right: 5px;
  }

  .show {
    display: block;
  }
}

@media (min-width: 1024px) {
  :deep(.desktop-margin) {
    margin-left: 274px;
    margin-right: 274px;
  }
}
</style>
